import React, { useState } from 'react';
import { 
  Receipt, 
  Calendar, 
  User, 
  Table, 
  CreditCard, 
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  DollarSign,
  Percent,
  Hash,
  Eye
} from 'lucide-react';
import { SaleResource } from '../types';
import SaleDetailDialog from './SaleDetailDialog';

interface SaleCardProps {
  sale: SaleResource;
}

const SaleCard: React.FC<SaleCardProps> = ({ sale }) => {
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      paid: 'bg-green-100 text-green-800',
      unpaid: 'bg-yellow-100 text-yellow-800',
      void: 'bg-red-100 text-red-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status: string) => {
    const statusIcons: Record<string, React.ReactNode> = {
      paid: <CheckCircle className="h-4 w-4" />,
      unpaid: <Clock className="h-4 w-4" />,
      void: <XCircle className="h-4 w-4" />
    };
    return statusIcons[status] || <AlertCircle className="h-4 w-4" />;
  };

  const getPaymentMethodColor = (method: string) => {
    const methodColors: Record<string, string> = {
      cash: 'bg-green-100 text-green-800',
      debit: 'bg-blue-100 text-blue-800',
      online: 'bg-purple-100 text-purple-800',
      qris: 'bg-orange-100 text-orange-800'
    };
    return methodColors[method] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
      {/* Sale Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center mb-2">
              <Receipt className="h-5 w-5 text-primary-600 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900 truncate">
                {sale.number}
              </h3>
            </div>
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <div className="flex items-center">
                <Hash className="h-4 w-4 mr-1" />
                <span>#{sale.queueNumber}</span>
              </div>
              {sale.tableNumber && (
                <div className="flex items-center">
                  <Table className="h-4 w-4 mr-1" />
                  <span>Table {sale.tableNumber}</span>
                </div>
              )}
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-1" />
                <span>{formatDate(sale.date)}</span>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end space-y-2 ml-4">
            {/* Status Badge */}
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(sale.status)}`}>
              {getStatusIcon(sale.status)}
              <span className="ml-1 capitalize">{sale.status}</span>
            </span>
            
            {/* Payment Method */}
            {sale.paymentMethod && (
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPaymentMethodColor(sale.paymentMethod)}`}>
                <CreditCard className="h-3 w-3 mr-1" />
                {sale.paymentMethod}
              </span>
            )}
          </div>
        </div>

        {/* Customer Info */}
        {sale.customerName && (
          <div className="flex items-center text-sm text-gray-600 mb-2">
            <User className="h-4 w-4 mr-2 text-gray-400" />
            <span>{sale.customerName}</span>
          </div>
        )}

        {/* Note */}
        {sale.note && (
          <p className="text-sm text-gray-600 italic">"{sale.note}"</p>
        )}
      </div>

      {/* Sale Details */}
      <div className="p-4 space-y-3">
        {/* Financial Summary */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Subtotal:</span>
            <span className="ml-2 font-medium text-gray-900">{formatCurrency(sale.totalAmount)}</span>
          </div>
          <div>
            <span className="text-gray-500">Grand Total:</span>
            <span className="ml-2 font-medium text-green-600">{formatCurrency(sale.grandTotal)}</span>
          </div>
        </div>

        {/* Discounts and Charges */}
        {(sale.discountAmount > 0 || sale.otherExpenseAmount > 0 || sale.ppnAmount > 0 || sale.serviceChargeAmount > 0) && (
          <div className="pt-3 border-t border-gray-100 space-y-2 text-xs">
            {sale.discountAmount > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-500">Discount ({sale.discountName || 'General'}):</span>
                <span className="text-red-600">-{formatCurrency(sale.discountAmount)}</span>
              </div>
            )}
            {sale.otherExpenseAmount > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-500">Other Expense ({sale.otherExpenseName}):</span>
                <span className="text-gray-600">+{formatCurrency(sale.otherExpenseAmount)}</span>
              </div>
            )}
            {sale.ppnAmount > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-500">Pajak ({sale.ppnPercent}%):</span>
                <span className="text-gray-600">+{formatCurrency(sale.ppnAmount)}</span>
              </div>
            )}
            {sale.serviceChargeAmount > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-500">Service Charge ({sale.serviceChargeRate}%):</span>
                <span className="text-gray-600">+{formatCurrency(sale.serviceChargeAmount)}</span>
              </div>
            )}
            {sale.roundingAmount !== 0 && (
              <div className="flex justify-between">
                <span className="text-gray-500">Rounding:</span>
                <span className={sale.roundingAmount > 0 ? 'text-gray-600' : 'text-red-600'}>
                  {sale.roundingAmount > 0 ? '+' : ''}{formatCurrency(sale.roundingAmount)}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Payment Info */}
        <div className="pt-3 border-t border-gray-100">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Paid Amount:</span>
              <span className="ml-2 font-medium text-green-600">{formatCurrency(sale.paidAmount)}</span>
            </div>
            {sale.changeAmount > 0 && (
              <div>
                <span className="text-gray-500">Change:</span>
                <span className="ml-2 font-medium text-blue-600">{formatCurrency(sale.changeAmount)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Additional Info */}
        <div className="pt-3 border-t border-gray-100">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center">
              <span className="mr-2">Products:</span>
              <span className="font-medium">{sale.totalQtyProduct} items</span>
            </div>
            {sale.referenceNumber && (
              <div className="flex items-center">
                <span className="mr-2">Ref:</span>
                <span className="font-mono bg-gray-100 px-2 py-1 rounded">{sale.referenceNumber}</span>
              </div>
            )}
          </div>
        </div>

        {/* View Detail Button */}
        <div className="pt-3 border-t border-gray-100">
          <button
            onClick={() => setShowDetailDialog(true)}
            className="w-full flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <Eye className="h-4 w-4 mr-2" />
            View Details
          </button>
        </div>

        {/* Void Reason */}
        {sale.status === 'void' && sale.voidReason && (
          <div className="pt-3 border-t border-gray-100">
            <div className="flex items-start text-xs text-red-600">
              <AlertCircle className="h-3 w-3 mr-2 mt-0.5 flex-shrink-0" />
              <span><strong>Void Reason:</strong> {sale.voidReason}</span>
            </div>
          </div>
        )}
      </div>

      {/* Sale Detail Dialog */}
      <SaleDetailDialog
        isOpen={showDetailDialog}
        onClose={() => setShowDetailDialog(false)}
        saleId={sale.id}
      />
    </div>
  );
};

export default SaleCard;
